import { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { ArrowLeft, CalendarDays, ChevronLeft, ChevronRight, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  useCreateWorkOrder,
  useWorkOrderTechnicians,
  useWorkOrders,
} from '@/features/service/hooks'
import { WorkOrderForm } from '@/features/service/work-order-form'
import { cn } from '@/lib/utils'
import type { WorkOrder, WorkOrderPriority, WorkOrderWriteBody } from '@/types/service'

type CalendarViewMode = 'day' | 'week' | 'month'

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const MAX_CHIPS_PER_VIEW: Record<CalendarViewMode, number> = {
  day: Number.POSITIVE_INFINITY,
  week: 8,
  month: 3,
}

const CELL_MIN_HEIGHT: Record<CalendarViewMode, string> = {
  day: 'min-h-[360px]',
  week: 'min-h-[160px]',
  month: 'min-h-[104px]',
}

const PRIORITY_RANK: Record<WorkOrderPriority, number> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
}

const priorityChipClass: Record<WorkOrderPriority, string> = {
  urgent: 'border-red-300 bg-red-100 text-red-900 hover:bg-red-200',
  high: 'border-orange-300 bg-orange-100 text-orange-900 hover:bg-orange-200',
  medium: 'border-blue-300 bg-blue-100 text-blue-900 hover:bg-blue-200',
  low: 'border-border bg-muted text-muted-foreground hover:bg-accent',
}

const legendItems: { label: string; dotClass: string }[] = [
  { label: 'Urgent', dotClass: 'bg-red-400' },
  { label: 'High', dotClass: 'bg-orange-400' },
  { label: 'Medium', dotClass: 'bg-blue-400' },
  { label: 'Low', dotClass: 'bg-muted-foreground/40' },
]

// Local-time YYYY-MM-DD key, matching <input type="date"> values.
const dayKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate())

const addDays = (d: Date, n: number) => {
  const next = new Date(d)
  next.setDate(next.getDate() + n)
  return next
}

// A work order appears on the calendar at its scheduled date, falling back
// to its due date — the same anchor the backend's from/to filters use.
const anchorDate = (wo: WorkOrder): Date | null => {
  const raw = wo.scheduled_date ?? wo.due_date
  if (!raw) return null
  const d = new Date(raw)
  return Number.isNaN(d.getTime()) ? null : d
}

export function CalendarView() {
  const [view, setView] = useState<CalendarViewMode>('month')
  // The date the calendar is centered on; prev/next move it by the current
  // view's step (one day, one week, or one month).
  const [cursor, setCursor] = useState(() => startOfDay(new Date()))
  const [technician, setTechnician] = useState('all')
  const [createDay, setCreateDay] = useState<string | null>(null)

  const { data: technicians = [] } = useWorkOrderTechnicians()

  // Cells of the visible grid: a single day, one Sun–Sat week, or the
  // 6-week month grid starting on Sunday.
  const cells = useMemo(() => {
    if (view === 'day') return [cursor]
    if (view === 'week') {
      const start = addDays(cursor, -cursor.getDay())
      return Array.from({ length: 7 }, (_, i) => addDays(start, i))
    }
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1)
    const start = addDays(first, -first.getDay())
    return Array.from({ length: 42 }, (_, i) => addDays(start, i))
  }, [view, cursor])

  // Fetch exactly the visible span, sent as RFC3339 instants so the backend
  // compares absolute timestamps. The month grid covers parts of adjacent
  // months, so its range is wider than the month itself — otherwise those
  // leading/trailing days would always render empty.
  const range = useMemo(() => {
    const first = cells[0]
    const last = cells[cells.length - 1]
    return {
      from: first,
      to: new Date(last.getFullYear(), last.getMonth(), last.getDate(), 23, 59, 59, 999),
    }
  }, [cells])

  const {
    data: workOrders = [],
    isLoading,
    isError,
  } = useWorkOrders({
    from: range.from.toISOString(),
    to: range.to.toISOString(),
    technician: technician === 'all' ? undefined : technician,
  })

  const eventsByDay = useMemo(() => {
    const map = new Map<string, WorkOrder[]>()
    for (const wo of workOrders) {
      const d = anchorDate(wo)
      if (!d) continue
      const key = dayKey(d)
      const list = map.get(key) ?? []
      list.push(wo)
      map.set(key, list)
    }
    for (const list of map.values()) {
      list.sort(
        (a, b) =>
          PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority] || a.title.localeCompare(b.title),
      )
    }
    return map
  }, [workOrders])

  const todayKey = dayKey(new Date())

  const periodLabel = useMemo(() => {
    if (view === 'month') {
      return cursor.toLocaleDateString(undefined, {
        month: 'long',
        year: 'numeric',
      })
    }
    if (view === 'day') {
      return cursor.toLocaleDateString(undefined, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    }
    const start = cells[0]
    const end = cells[cells.length - 1]
    if (start.getFullYear() !== end.getFullYear()) {
      const opts = { month: 'short', day: 'numeric', year: 'numeric' } as const
      return `${start.toLocaleDateString(undefined, opts)} – ${end.toLocaleDateString(undefined, opts)}`
    }
    const sameMonth = start.getMonth() === end.getMonth()
    const startStr = start.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    })
    const endStr = end.toLocaleDateString(
      undefined,
      sameMonth ? { day: 'numeric' } : { month: 'short', day: 'numeric' },
    )
    return `${startStr} – ${endStr}, ${end.getFullYear()}`
  }, [view, cursor, cells])

  const shiftPeriod = (delta: number) =>
    setCursor((c) => {
      if (view === 'month') return new Date(c.getFullYear(), c.getMonth() + delta, 1)
      return addDays(c, delta * (view === 'week' ? 7 : 1))
    })

  const goToday = () => setCursor(startOfDay(new Date()))

  // Clicking a cell's blank space filters the calendar to that day.
  const focusDay = (d: Date) => {
    setView('day')
    setCursor(startOfDay(d))
  }

  const chipTitle = (wo: WorkOrder) =>
    [wo.title, wo.boat_name, wo.assigned_technician, wo.status.replace('_', ' ')]
      .filter(Boolean)
      .join(' · ')

  const maxChips = MAX_CHIPS_PER_VIEW[view]
  const cellMinHeight = CELL_MIN_HEIGHT[view]
  const headerLabels =
    view === 'day' ? [cursor.toLocaleDateString(undefined, { weekday: 'long' })] : WEEKDAYS
  const headerGridClass = view === 'day' ? 'grid-cols-1' : 'grid-cols-7'
  const bodyGridClass =
    view === 'day'
      ? 'grid grid-cols-1 [&>*]:border-r-0 [&>*]:border-b-0'
      : 'grid grid-cols-7 [&>*:nth-child(7n)]:border-r-0 [&>*:nth-last-child(-n+7)]:border-b-0'

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <Link to="/service" className="inline-block">
            <Button variant="ghost" size="sm" className="-ml-2 gap-2">
              <ArrowLeft className="h-4 w-4" /> Back to Service
            </Button>
          </Link>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <CalendarDays className="h-8 w-8" /> Service Calendar
          </h1>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            aria-label={`Previous ${view}`}
            onClick={() => shiftPeriod(-1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            aria-label={`Next ${view}`}
            onClick={() => shiftPeriod(1)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToday}>
            Today
          </Button>
          <Tabs value={view} onValueChange={(v) => setView(v as CalendarViewMode)}>
            <TabsList aria-label="Calendar view">
              <TabsTrigger value="day">Day</TabsTrigger>
              <TabsTrigger value="week">Week</TabsTrigger>
              <TabsTrigger value="month">Month</TabsTrigger>
            </TabsList>
          </Tabs>
          <h2 className="ml-2 text-lg font-semibold">{periodLabel}</h2>
          {!isLoading && !isError && (
            <span className="text-sm text-muted-foreground">
              ({workOrders.length} work order{workOrders.length === 1 ? '' : 's'})
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={technician} onValueChange={setTechnician}>
            <SelectTrigger className="w-48" aria-label="Filter by technician">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All technicians</SelectItem>
              {technicians.map((m) => (
                <SelectItem key={m} value={m}>
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={() => setCreateDay(dayKey(view === 'day' ? cursor : new Date()))}>
            <Plus className="mr-1.5 h-4 w-4" /> New Work Order
          </Button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
        {legendItems.map((item) => (
          <span key={item.label} className="flex items-center gap-1.5">
            <span className={cn('h-2.5 w-2.5 rounded-full', item.dotClass)} />
            {item.label}
          </span>
        ))}
        <span className="flex items-center gap-1.5">
          <span className="line-through opacity-60">Completed</span>
        </span>
      </div>

      {isError ? (
        <div className="text-destructive">Failed to load the schedule.</div>
      ) : (
        <div className="overflow-x-auto">
          <div
            className={cn(
              'overflow-hidden rounded-lg border',
              view === 'day' ? 'min-w-[320px]' : 'min-w-[760px]',
            )}
          >
            <div className={cn('grid border-b bg-muted/50', headerGridClass)}>
              {headerLabels.map((w) => (
                <div key={w} className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                  {w}
                </div>
              ))}
            </div>
            <div className={bodyGridClass}>
              {isLoading
                ? Array.from({ length: cells.length }).map((_, i) => (
                    <div key={i} className={cn(cellMinHeight, 'border-b border-r p-1.5')}>
                      <Skeleton className="h-full w-full" />
                    </div>
                  ))
                : cells.map((d) => {
                    const key = dayKey(d)
                    const inRange = view !== 'month' || d.getMonth() === cursor.getMonth()
                    const events = eventsByDay.get(key) ?? []
                    const visible = events.slice(0, maxChips)
                    const extra = events.length - visible.length
                    return (
                      <div
                        key={key}
                        onClick={() => focusDay(d)}
                        className={cn(
                          cellMinHeight,
                          'border-b border-r p-1.5',
                          inRange ? 'bg-background' : 'bg-muted/40',
                          key === todayKey && 'bg-primary/5',
                          'cursor-pointer',
                        )}
                      >
                        <div className="mb-1 flex items-center justify-between">
                          <span
                            className={cn(
                              'text-xs font-medium',
                              !inRange && 'text-muted-foreground/60',
                              key === todayKey &&
                                'flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground',
                            )}
                          >
                            {d.getDate()}
                          </span>
                        </div>
                        <div className="flex flex-col gap-1">
                          {visible.map((wo) => (
                            <Link
                              key={wo.id}
                              to="/service/$workOrderId"
                              params={{ workOrderId: wo.id }}
                              onClick={(e) => e.stopPropagation()}
                              title={chipTitle(wo)}
                              className={cn(
                                'block truncate rounded border px-1.5 py-0.5 text-left text-xs',
                                priorityChipClass[wo.priority],
                                wo.status === 'completed' && 'opacity-60 line-through',
                              )}
                            >
                              {wo.title}
                            </Link>
                          ))}
                          {extra > 0 && (
                            <span className="px-1 text-[10px] text-muted-foreground">
                              +{extra} more
                            </span>
                          )}
                        </div>
                      </div>
                    )
                  })}
            </div>
          </div>
        </div>
      )}

      {/* Create dialog, prefilled with the viewed day in Day view (else today)
          and the active technician filter */}
      <Dialog open={createDay !== null} onOpenChange={(open) => !open && setCreateDay(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5" /> New Work Order
            </DialogTitle>
            <DialogDescription>Scheduled for {createDay}</DialogDescription>
          </DialogHeader>
          {createDay && (
            <CreateWorkOrderForm
              scheduledDate={createDay}
              technician={technician === 'all' ? '' : technician}
              onDone={() => setCreateDay(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

function CreateWorkOrderForm({
  scheduledDate,
  technician,
  onDone,
}: {
  scheduledDate: string
  technician: string
  onDone: () => void
}) {
  const createMutation = useCreateWorkOrder()
  const handleSubmit = (values: WorkOrderWriteBody) => {
    createMutation.mutate(values, { onSuccess: onDone })
  }
  return (
    <WorkOrderForm
      defaultValues={{
        scheduled_date: scheduledDate,
        assigned_technician: technician || undefined,
      }}
      onSubmit={handleSubmit}
      isPending={createMutation.isPending}
    />
  )
}
